import { useEffect, useMemo, useState } from 'react';
import { getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';

// TP-5: the predictive "adrenaline" simulator (spec §2). Drag the Target Profit %
// slider → instantly see the expected house edge, player win-rate, volatility
// ("adrenaline"), and a sample player-balance curve. All 21 slider stops are
// pre-computed by the backend so the graphs update with zero lag.

async function apiFetch(url: string) {
  const t = getAdminAccessToken();
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json', ...(t ? { authorization: `Bearer ${t}` } : {}) } });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error?.message || 'API Error');
  return res.json();
}

type Point = { pct: number; adrenaline: number; houseEdgePct: number; playerWinRatePct: number; swingRatePct: number; equity: number[] };
type Preview = { rounds: number; stake: number; points: Point[] };

function EquityChart({ equity }: { equity: number[] }) {
  const W = 640, H = 200, pad = 12;
  const min = Math.min(0, ...equity), max = Math.max(0, ...equity);
  const range = (max - min) || 1;
  const x = (i: number) => pad + (i / (equity.length - 1)) * (W - 2 * pad);
  const y = (v: number) => pad + (1 - (v - min) / range) * (H - 2 * pad);
  const path = equity.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const zeroY = y(0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48" preserveAspectRatio="none">
      <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="currentColor" strokeWidth="1" className="text-gray-300 dark:text-gray-600" strokeDasharray="4 4" />
      <path d={path} fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-500 dark:text-orange-400" />
    </svg>
  );
}

function adrenalineLabel(a: number) {
  if (a >= 0.66) return { text: 'HIGH — big win/loss swings', cls: 'text-orange-600 dark:text-orange-400' };
  if (a >= 0.33) return { text: 'MEDIUM — some fluctuation', cls: 'text-amber-600 dark:text-amber-400' };
  return { text: 'LOW — steady, fast losses', cls: 'text-sky-600 dark:text-sky-400' };
}

export default function TargetProfitSimulator() {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [pct, setPct] = useState(30);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/api/admin/reports/target-profit-preview').then(setPreview).catch((e) => setErr(e.message));
  }, []);

  const sel = useMemo(() => preview?.points.find((p) => p.pct === pct) || null, [preview, pct]);
  const ad = sel ? adrenalineLabel(sel.adrenaline) : null;

  return (
    <div className="max-w-4xl mx-auto p-4 text-gray-900 dark:text-gray-100">
      <h1 className="text-2xl font-bold">Target-Profit — Predictive Simulator</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Move the slider to preview how a game will feel at each profit target — before you set it live. Modelled from the real deterministic engine. Cricket excluded.
      </p>
      {err && <div className="mb-4 p-3 rounded-md bg-red-500/10 text-red-700 dark:text-red-400 text-sm">{err}</div>}

      {/* Slider */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold">Target profit</label>
          <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{pct}%</span>
        </div>
        <input type="range" min={0} max={100} step={5} value={pct}
          onChange={(e) => setPct(Number(e.target.value))}
          className="w-full accent-indigo-600" />
        <div className="flex justify-between text-xs text-gray-400 mt-1"><span>0% (fair / thrilling)</span><span>100% (always loses)</span></div>
      </div>

      {sel && ad && (
        <>
          {/* Adrenaline + stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Stat label="Adrenaline" value={`${Math.round(sel.adrenaline * 100)}`} suffix="/100" cls={ad.cls} sub={ad.text} />
            <Stat label="House edge" value={`${sel.houseEdgePct}`} suffix="%" cls="text-emerald-600 dark:text-emerald-400" sub="of user bets kept" />
            <Stat label="Player win rate" value={`${sel.playerWinRatePct}`} suffix="%" cls="text-sky-600 dark:text-sky-400" sub="rounds the player wins" />
            <Stat label="Swings" value={`${sel.swingRatePct}`} suffix="%" cls="text-orange-600 dark:text-orange-400" sub="win↔loss flips" />
          </div>

          {/* Equity curve */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-sm font-semibold mb-1">A player's balance over ~{preview?.rounds} rounds</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Low targets wiggle (wins &amp; losses keep it exciting); high targets slide straight down.
            </div>
            <EquityChart equity={sel.equity} />
          </div>
        </>
      )}
      {!preview && !err && <div className="text-sm text-gray-500">Loading predictive model…</div>}
    </div>
  );
}

function Stat({ label, value, suffix, cls, sub }: { label: string; value: string; suffix?: string; cls: string; sub: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-2xl font-bold ${cls}`}>{value}<span className="text-sm font-normal opacity-70">{suffix}</span></div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{sub}</div>
    </div>
  );
}
